/* eslint-disable camelcase */

import { useState, FC } from 'react';
import { coreService } from 'network-utils';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { translate } from 'translations';
import { showAlert } from '@redux/slices/informationSlice';
import { selectToken } from '@redux/slices/userSlice';
import { mixpanelTrack, gainsightTrack } from 'utils';
import ContactSupport from './contactSupport';

const ContactSupportContainer: FC<{
    setIsContactSupportOpen: Function;
    defaultValues?: { subject: string; description: string };
    redirectToHomeOnClose?: boolean;
    isUpgrade?: boolean;
}> = ({
    setIsContactSupportOpen,
    defaultValues,
    redirectToHomeOnClose,
    isUpgrade
}: {
    setIsContactSupportOpen: Function;
    defaultValues?: { subject: string; description: string };
    redirectToHomeOnClose?: boolean;
    isUpgrade?: boolean;
}) => {
    const [IsFormSent, setIsFormSent] = useState<boolean>(false);
    const services = coreService.contactSupportAPI;
    const generalError = translate('Something went wrong, please try again later');
    const dispatch = useDispatch();
    const token = useSelector(selectToken);
    const navigate = useNavigate();

    const submitUserContactSupport = async (input: {
        subject: string;
        description: string;
        priority: string;
        productArea: string;
    }): Promise<{ success: boolean; error?: string }> => {
        setIsFormSent(false);
        const result = await services.contactSupport(token, input);
        return result;
    };

    const closeContactSupport = () => {
        setIsContactSupportOpen(false);
        redirectToHomeOnClose && navigate('/app/home');
    };

    const showError = (error: string) => {
        dispatch(
            showAlert({
                value: error,
                status: 'error'
            })
        );
    };

    const { mutate: onSubmit, isLoading } = useMutation(submitUserContactSupport, {
        onSuccess: (result: { success: boolean; error?: string }, variables) => {
            if (result.success) {
                mixpanelTrack({
                    eventName: 'Contact Form Submitted',
                    properties: {
                        subject: variables.subject,
                        description: variables.description,
                        priority: variables.priority
                    }
                });
                gainsightTrack('Contact Form Submitted', {
                    subject: variables.subject,
                    description: variables.description,
                    priority: variables.priority
                });
                setIsFormSent(true);
                return;
            }
            showError(result?.error || generalError);
        },
        onError: () => {
            showError(generalError);
        }
    });

    return (
        <ContactSupport
            onSubmit={(subject: string, description: string, urgency: string) =>
                onSubmit({ subject, description, priority: urgency, productArea: 'general' })
            }
            isFormSent={IsFormSent}
            isLoading={isLoading}
            onClose={() => closeContactSupport()}
            defaultValues={defaultValues}
            isUpgrade={isUpgrade}
        />
    );
};

export default ContactSupportContainer;
